import {
  Link,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "@remix-run/react";
import { useTranslation } from "react-i18next";

import Button from "~/components/atoms/Button";
import Typo from "~/components/atoms/Typo";
import CheckboxGroup from "~/components/molecules/CheckboxGroup/CheckboxGroup";
import { IOption } from "~/components/molecules/CheckboxGroup/type";
import FilterSidebarRange from "~/components/molecules/FilterSideBar/FilterSidebarRange";
import { Form, TFormProps } from "~/components/templates/form";
import { loader } from "~/routes/($lang).projects._index/route";
import { ETypoColor } from "~/utilities/enums/Colors";
import { EMarketSegment } from "~/utilities/enums/marketSegment";
import { EProjectStatus } from "~/utilities/enums/ProjectStatus";
import { Slug } from "~/utilities/enums/Slug";
import { ETypoTag, ETypoVariant } from "~/utilities/enums/Typo";
import {
  filterSideBarSchema,
  TFilterSideBarFormData,
} from "~/utilities/schema/filterSideBar";

export interface IFilterSideBarProps {
  defaultValues?: TFilterSideBarFormData;
  mode?: TFormProps<any>["submitMode"];
  onClose?: () => void;
}

const FilterSideBarMobile: React.FC<IFilterSideBarProps> = ({
  defaultValues,
  mode = "onChange",
  onClose,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { filterOptions } = useLoaderData<typeof loader>();

  const categoryOptions = filterOptions.categories;
  const getParamArray = (param: string | null): string[] =>
    param ? param.split(",") : [];
  const defaultFormValues: Partial<TFilterSideBarFormData> = {
    marketSegment: getParamArray(searchParams.get("marketSegment")),
    projectType: getParamArray(searchParams.get("projectType")),
    status: getParamArray(searchParams.get("status")),
    unitSizeMax: searchParams.get("unitSizeMax") || "0",
    unitSizeMin: searchParams.get("unitSizeMin") || "0",
    unitPSFMin: searchParams.get("unitPSFMin") || "0",
    unitPSFMax: searchParams.get("unitPSFMax") || "0",
    ...defaultValues,
  };

  const checkbox_options: {
    name: keyof TFilterSideBarFormData;
    label: string;
    options: IOption[];
  }[] = [
    {
      name: "marketSegment",
      label: t("marketSegment"),
      options: [
        { id: EMarketSegment.CCR, name: EMarketSegment.CCR },
        { id: EMarketSegment.OCR, name: EMarketSegment.OCR },
        { id: EMarketSegment.RCR, name: EMarketSegment.RCR },
      ],
    },
    {
      name: "projectType",
      label: t("projectType"),
      options: categoryOptions,
    },
    {
      name: "status",
      label: t("status"),
      options: [
        { id: EProjectStatus.BY_BUC, name: t(EProjectStatus.BY_BUC) },
        { id: EProjectStatus.TOP_SOON, name: t(EProjectStatus.TOP_SOON) },
        { id: EProjectStatus.COMPLETED, name: t(EProjectStatus.COMPLETED) },
      ],
    },
  ];

  const location = useLocation();

  return (
    <>
      <Form<TFilterSideBarFormData>
        validationSchema={filterSideBarSchema}
        useFormProps={{ mode: mode, defaultValues: defaultFormValues }}
        submitMode={mode}
        action={location.pathname + location.search}
      >
        {({ register, handleSubmit }) => (
          <div className="flex flex-col gap-6 rounded-xl border-none bg-filterBarDrawerBackground p-6">
            {mode !== "onSubmit" && (
              <>
                <div className="flex items-center justify-between">
                  <Typo
                    color={ETypoColor.TEXT}
                    tag={ETypoTag.P}
                    variant={ETypoVariant.HEADER_24}
                  >
                    {t("filter")}
                  </Typo>
                  <Typo
                    tag={ETypoTag.P}
                    variant={ETypoVariant.BODY_TITLE_16}
                    color={ETypoColor.ERROR}
                    className="cursor-pointer"
                  >
                    <Link prefetch="render" to={Slug.PROJECTS}>
                      {t("clear_all")}
                    </Link>
                  </Typo>
                </div>
                <div className="divider"></div>
              </>
            )}
            {checkbox_options?.map(({ name, options, label }) => (
              <CheckboxGroup
                key={name}
                label={label}
                options={options}
                register={register(name)}
              />
            ))}
            <FilterSidebarRange
              label={t("unit_size")}
              inputMinProps={{
                placeholder: t("min_size"),
                ...register("unitSizeMin"),
              }}
              inputMaxProps={{
                placeholder: t("max_size"),
                ...register("unitSizeMax"),
              }}
            />
            <FilterSidebarRange
              label={t("unit_PSF")}
              inputMinProps={{
                placeholder: t("min"),
                ...register("unitPSFMin"),
              }}
              inputMaxProps={{
                placeholder: t("max"),
                ...register("unitPSFMax"),
              }}
            />
            {mode === "onSubmit" && (
              <div className="fixed bottom-4 hidden justify-center self-center lg:flex">
                <Button
                  type="submit"
                  className="lg:px-13"
                  onClick={(e) => {
                    handleSubmit(e);
                    onClose?.();
                  }}
                >
                  <Typo
                    className="whitespace-nowrap font-normal capitalize"
                    tag={ETypoTag.SPAN}
                    variant={ETypoVariant.BODY_TITLE_16}
                    color={ETypoColor.WHITE}
                  >
                    {t("filter")}
                  </Typo>
                </Button>
              </div>
            )}
          </div>
        )}
      </Form>
    </>
  );
};

export default FilterSideBarMobile;
